import { FC } from 'react';
import styled, { css } from 'styled-components';
import * as tokens from '@seed-health/tokens';
import { motion } from 'motion/react';

import { ArrowRight } from 'components/library/svgs--foldertentative/arrow-right';
import { ease, duration } from 'styled/tools/motion';
import Link from 'next/link';

type colors = 'seedgreen' | 'snowwhite' | 'frostedglass';
type sizes = 'large' | 'medium' | 'small';

export interface ButtonProps {
  id?: string;
  label?: string;
  url?: string;
  newWindow?: boolean;
  color?: colors;
  hoverColor?: colors;
  size?: sizes;
  type?: 'submit' | 'reset' | 'button';
  onClick?: () => void;
  children?: React.ReactNode | string;
  className?: string;
  disabled?: boolean;
  animated?: boolean;
  // optional prop that allows you to simulate hover animation state without hovering
  // ie if the parent of the button is active
  showArrow?: boolean;
  as?: keyof JSX.IntrinsicElements;
  testid?: string;
}

const IconWrapper = styled(motion.span)`
  display: flex;
  align-items: center;
`;

const buttonBase = css`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-full);
  border: none;
  cursor: pointer;
  text-align: center;
  font-family: var(--ff, 'Seed Sans');
  font-style: normal;
  font-weight: 350;
  line-height: normal;
  letter-spacing: -0.0025em;
  text-decoration: none;
`;

const getButtonColors = (color: 'seedgreen' | 'snowwhite' | 'frostedglass') => {
  switch (color) {
    case 'seedgreen':
      return {
        background: 'var(--fill-brand)',
        textColor: 'var(--fill-primary)',
        focusColor: 'var(--fill-brand)',
        activeBackground: '#142f0c',
        focusBorderColor: 'var(--fill-primary)',
        focusBoxShadowColor: 'var(--fill-brand)',
        arrowColor: 'var(--fill-primary)',
      };
    case 'snowwhite':
      return {
        background: 'var(--fill-primary)',
        textColor: 'var(--fill-brand)',
        focusColor: 'var(--fill-primary)',
        activeBackground: 'var(--fill-tertiary)',
        focusBorderColor: 'var(--fill-brand)',
        focusBoxShadowColor: '#fcfcf780',
        arrowColor: 'var(--fill-brand)',
      };
    case 'frostedglass':
      return {
        background: 'var(--color-neutral-frosted-glass-t35)',
        textColor: 'var(--fill-primary)',
        focusColor: 'var(--fill-brand)',
        activeBackground: 'var(--scrim-glass-dark-35)',
        focusBorderColor: 'var(--fill-primary)',
        focusBoxShadowColor: 'var(--fill-brand)',
        arrowColor: 'var(--fill-primary)',
      };
    default:
      throw new Error(`Unsupported Button color: ${color}`);
  }
};

const getButtonSizeStyles = (size: 'large' | 'medium' | 'small') => {
  switch (size) {
    case 'large':
      return {
        padding: 'var(--spacing--x2, 16px) var(--spacing--x3, 24px)',
        fontSize: tokens.BodyMedBold.fontSize,
      };
    case 'medium':
      return {
        padding: '10px 16px',
        fontSize: tokens.BodySmBold.fontSize,
      };
    case 'small':
      return {
        fontSize: tokens.BodySmBold.fontSize,
        padding: '10px',
        aspectRatio: '1/1',
      };
    default:
      throw new Error(`Unsupported Button size: ${size}`);
  }
};

const buttonStyles = css<{ color: colors; hoverColor?: colors; size: sizes }>`
  ${buttonBase}
  ${({ color, hoverColor }) => {
    const { background, textColor, focusColor } = getButtonColors(color);
    const {
      activeBackground: activeBackground,
      background: hoverBackground,
      textColor: hoverTextColor,
      focusColor: hoverFocusColor,
      focusBorderColor: focusBorderColor,
      focusBoxShadowColor: focusBoxShadowColor,
    } = getButtonColors(hoverColor || color);
    return `
      --focus-color: ${focusColor};
      background: ${background};
      color: ${textColor};

      &:focus,
      &:focus-visible {
        border: 3px solid ${focusBorderColor};
        box-shadow: 0 0 0 0.5px ${focusBoxShadowColor};
      }

      &:focus-visible {
        outline: none;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      &:hover,
      &:focus,
      &:active {
        color: ${hoverTextColor};
        background: ${hoverBackground};
        --focus-color: ${hoverFocusColor};
      }

      &:active {
        background: ${activeBackground};
      }

      &:hover ${IconWrapper},
      &:focus ${IconWrapper},
      &:active ${IconWrapper} {
        margin-left: 8px;
      }
    `;
  }}
  ${({ size }) => {
    // Note: including aspect ratio for small buttons, even though its defined, causes mismatch with Figma design
    const { padding, fontSize, aspectRatio } = getButtonSizeStyles(size);
    return `
      padding: ${padding};
      --fz: ${fontSize};
      font-size: var(--fz);
      // ${aspectRatio ? `aspect-ratio: ${aspectRatio};` : ''}
    `;
  }}
`;

const ButtonWrapper = styled(motion.button)<{
  color: colors;
  size: sizes;
  animated?: boolean;
  hoverColor?: colors;
}>`
  ${buttonStyles}
  gap: ${({ animated }) => (animated ? '0' : '8px')};
`;

const LinkWrapper = styled(motion(Link))<{
  color: colors;
  size: sizes;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}>`
  ${buttonStyles}
`;

const arrowVariants = {
  start: {
    opacity: 0,
    x: '-8px',
    width: '0px',
    marginLeft: '0px',
    transition: {
      ease,
      duration,
    },
  },
  hover: {
    opacity: 1,
    x: '4px',
    width: '12px',
    marginLeft: '8px',
    transition: {
      ease,
      duration,
    },
  },
  focus: {
    opacity: 1,
    x: '4px',
    width: '12px',
    marginLeft: '8px',
    transition: {
      ease,
      duration,
    },
  },
  active: {
    opacity: 1,
    x: '4px',
    width: '12px',
    marginLeft: '8px',
    transition: {
      ease,
      duration,
    },
  },
};

const Button: FC<ButtonProps> = ({
  id,
  label,
  children,
  url,
  newWindow = false,
  color = 'seedgreen',
  size = 'large',
  type = 'button',
  onClick,
  className,
  disabled,
  animated,
  showArrow,
  hoverColor,
  as,
  testid,
}) => {
  const commonProps = {
    color,
    size,
    onClick,
    className,
    animated,
    as,
    initial: animated ? 'start' : undefined,
    whileHover: animated ? 'hover' : undefined,
    hoverColor: hoverColor,
  };

  if (url) {
    return (
      <LinkWrapper
        id={id}
        {...commonProps}
        href={url}
        target={newWindow ? '_blank' : undefined}
        data-testid={testid}
        whileFocus={animated ? 'focus' : undefined}
        whileTap={animated ? 'active' : undefined}
      >
        {children}
        {label}
        {animated && (
          <IconWrapper
            variants={arrowVariants}
            animate={showArrow ? 'hover' : undefined}
          >
            <ArrowRight
              color={
                getButtonColors(commonProps.hoverColor || commonProps.color)
                  .arrowColor
              }
              style={{ width: '12px', height: '12px' }}
            />
          </IconWrapper>
        )}
      </LinkWrapper>
    );
  }

  return (
    <ButtonWrapper
      {...commonProps}
      type={type}
      disabled={disabled}
      id={id}
      data-testid={testid}
      whileFocus={animated ? 'focus' : undefined}
      whileTap={animated ? 'active' : undefined}
    >
      {children}
      {label}
      {animated && (
        <IconWrapper
          variants={arrowVariants}
          animate={showArrow ? 'hover' : undefined}
        >
          <ArrowRight
            color={
              getButtonColors(commonProps.hoverColor || commonProps.color)
                .arrowColor
            }
            style={{ width: '12px', height: '12px' }}
          />
        </IconWrapper>
      )}
    </ButtonWrapper>
  );
};

export default Button;
